import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useGetAdminCategoryQuery,
  useGetAdminCategoriesQuery,
} from '../../store/api/adminApi';
import { useLanguage } from '../../hooks/useLanguage';

export function AdminCategoryFormPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const isEditMode = !!categoryId;

  // Get category data if editing (admin API: api/v1/admin/categories/:id)
  const { data: categoryData, isLoading: categoryLoading } = useGetAdminCategoryQuery(
    categoryId || '',
    { skip: !isEditMode }
  );

  // Get all categories for parent selection (الفئة الأب) – admin API
  const { data: categoriesData, isLoading: categoriesLoading } = useGetAdminCategoriesQuery(
    { includeHidden: true }
  );

  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    description: '',
    descriptionEn: '',
    price: '',
    cost: '',
    cashbackPercentage: '',
    isEnabledCashback: false,
    isHidden: false,
    isParent: false,
    parentId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load category data when editing
  useEffect(() => {
    if (isEditMode && categoryData?.data.category) {
      const category = categoryData.data.category;
      setFormData({
        name: category.name || '',
        nameEn: category.nameEn || '',
        description: category.description || '',
        descriptionEn: category.descriptionEn || '',
        price: category.price.toString(),
        cost: category.cost?.toString() || '',
        cashbackPercentage: category.cashbackPercentage != null ? category.cashbackPercentage.toString() : '',
        isEnabledCashback: category.isEnabledCashback || false,
        isHidden: category.isHidden || false,
        isParent: category.isParent || false,
        parentId: category.parentId || '',
      });
    }
  }, [isEditMode, categoryData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('admin.category.form.errors.nameRequired');
    }
    if (!formData.nameEn.trim()) {
      newErrors.nameEn = t('admin.category.form.errors.nameEnRequired');
    }
    if (!formData.price || Number.parseFloat(formData.price) < 0) {
      newErrors.price = t('admin.category.form.errors.priceInvalid');
    }
    if (!formData.cost || Number.parseFloat(formData.cost) < 0) {
      newErrors.cost = t('admin.category.form.errors.costInvalid');
    }
    if (formData.cashbackPercentage !== '') {
      const val = Number.parseFloat(formData.cashbackPercentage);
      if (Number.isNaN(val) || val < 0 || val > 1) {
        newErrors.cashbackPercentage = t('admin.category.form.errors.cashbackInvalid');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('admin.category.form.errors.validationFailed'));
      return;
    }

    try {
      const cashbackVal = formData.cashbackPercentage.trim() === ''
        ? 0
        : Number.parseFloat(formData.cashbackPercentage);
      const submitData = {
        name: formData.name.trim(),
        nameEn: formData.nameEn.trim(),
        description: formData.description.trim() || undefined,
        descriptionEn: formData.descriptionEn.trim() || undefined,
        price: Number.parseFloat(formData.price),
        cost: Number.parseFloat(formData.cost),
        cashbackPercentage: cashbackVal,
        isEnabledCashback: formData.isEnabledCashback,
        isHidden: formData.isHidden,
        isParent: formData.isParent,
        parentId: formData.parentId || null,
      };

      if (isEditMode && categoryId) {
        await updateCategory({ categoryId, data: submitData }).unwrap();
        toast.success(t('admin.category.form.updateSuccess'));
      } else {
        await createCategory(submitData).unwrap();
        toast.success(t('admin.category.form.createSuccess'));
      }

      navigate('/admin/categories');
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.category.form.submitError')
      );
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (isEditMode && categoryLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  // عرض كل الفئات الراجعة من الـ API؛ في وضع التعديل نستبعد الفئة الحالية (لا يمكن أن تكون الفئة أباً لنفسها)
  const allCategories = categoriesData?.data?.categories ?? [];
  const parentCategories = isEditMode && categoryId
    ? allCategories.filter((cat) => cat._id !== categoryId)
    : allCategories;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {isEditMode ? t('admin.category.form.editTitle') : t('admin.category.form.createTitle')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {isEditMode
            ? t('admin.category.form.editSubtitle')
            : t('admin.category.form.createSubtitle')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card-glass rounded-2xl shadow-2xl p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name (Arabic) */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.category.form.name')} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent ${
                errors.name ? 'border-danger' : 'border-neutral-dark'
              }`}
              placeholder={t('admin.category.form.namePlaceholder')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-danger font-body">{errors.name}</p>
            )}
          </div>

          {/* Name (English) */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.category.form.nameEn')} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleChange}
              className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent ${
                errors.nameEn ? 'border-danger' : 'border-neutral-dark'
              }`}
              placeholder={t('admin.category.form.nameEnPlaceholder')}
            />
            {errors.nameEn && (
              <p className="mt-1 text-sm text-danger font-body">{errors.nameEn}</p>
            )}
          </div>

          {/* Description (Arabic) */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.category.form.description')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent resize-none"
              placeholder={t('admin.category.form.descriptionPlaceholder')}
            />
          </div>

          {/* Description (English) */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.category.form.descriptionEn')}
            </label>
            <textarea
              name="descriptionEn"
              value={formData.descriptionEn}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent resize-none"
              placeholder={t('admin.category.form.descriptionEnPlaceholder')}
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.category.form.price')} <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="0.01"
              min="0"
              className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent ${
                errors.price ? 'border-danger' : 'border-neutral-dark'
              }`}
              placeholder="0.00"
            />
            {errors.price && (
              <p className="mt-1 text-sm text-danger font-body">{errors.price}</p>
            )}
          </div>

          {/* Cost */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.category.form.cost')} <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              name="cost"
              value={formData.cost}
              onChange={handleChange}
              step="0.01"
              min="0"
              className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent ${
                errors.cost ? 'border-danger' : 'border-neutral-dark'
              }`}
              placeholder="0.00"
            />
            {errors.cost && (
              <p className="mt-1 text-sm text-danger font-body">{errors.cost}</p>
            )}
          </div>

          {/* Cashback Percentage (اختياري) */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.category.form.cashbackPercentage')}
            </label>
            <input
              type="number"
              name="cashbackPercentage"
              value={formData.cashbackPercentage}
              onChange={handleChange}
              step="0.01"
              min="0"
              max="1"
              className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent ${
                errors.cashbackPercentage ? 'border-danger' : 'border-neutral-dark'
              }`}
              placeholder="0.05"
            />
            <p className="mt-1 text-xs text-neutral-medium font-body">
              {t('admin.category.form.cashbackHint')}
            </p>
            {errors.cashbackPercentage && (
              <p className="mt-1 text-sm text-danger font-body">{errors.cashbackPercentage}</p>
            )}
          </div>

          {/* Parent Category (الفئة الأب) - تعرض الفئات الراجعة من الـ API وعند الاختيار تُرسل مع طلب الإنشاء/التعديل */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.category.form.parentCategory')}
            </label>
            <select
              name="parentId"
              value={formData.parentId}
              onChange={handleChange}
              disabled={categoriesLoading}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent disabled:opacity-60"
            >
              <option value="">{t('admin.category.form.noParent')}</option>
              {categoriesLoading ? (
                <option value="" disabled>{t('common.loading')}</option>
              ) : (
                parentCategories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {currentLanguage === 'ar' ? cat.name : cat.nameEn}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Is Parent */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="isParent"
              id="isParent"
              checked={formData.isParent}
              onChange={handleChange}
              className="w-4 h-4 text-brand-accent bg-neutral-dark border-neutral-dark rounded focus:ring-brand-accent"
            />
            <label htmlFor="isParent" className="text-sm text-neutral-medium font-body cursor-pointer">
              {t('admin.category.form.isParent')}
            </label>
          </div>

          {/* Is Enabled Cashback */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="isEnabledCashback"
              id="isEnabledCashback"
              checked={formData.isEnabledCashback}
              onChange={handleChange}
              className="w-4 h-4 text-brand-accent bg-neutral-dark border-neutral-dark rounded focus:ring-brand-accent"
            />
            <label
              htmlFor="isEnabledCashback"
              className="text-sm text-neutral-medium font-body cursor-pointer"
            >
              {t('admin.category.form.isEnabledCashback')}
            </label>
          </div>

          {/* Is Hidden */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="isHidden"
              id="isHidden"
              checked={formData.isHidden}
              onChange={handleChange}
              className="w-4 h-4 text-brand-accent bg-neutral-dark border-neutral-dark rounded focus:ring-brand-accent"
            />
            <label htmlFor="isHidden" className="text-sm text-neutral-medium font-body cursor-pointer">
              {t('admin.category.form.isHidden')}
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-neutral-dark">
          <button
            type="submit"
            disabled={isCreating || isUpdating}
            className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
          >
            {isCreating || isUpdating ? (
              <>
                <ButtonSpinner />
                <span>{t('common.loading')}</span>
              </>
            ) : isEditMode ? (
              t('admin.category.form.updateButton')
            ) : (
              t('admin.category.form.createButton')
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/categories')}
            className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

